import * as React from 'react';
import { Image, StyleSheet, View } from 'react-native';

import color from '../../../style/color';
import { getRegion, Region } from '../../../lib/auth/region';

// Set to true by VerticalInstructions so the shared Instruction* components render their "version B"
// styling (plain black step numbers, no inline pills/icons) without affecting the control (animation) group.
export const VerticalInstructionsVariantContext = React.createContext(false);

// "Version B" vertical instruction screens (GRW-252 A/B test).
// Each step pairs an instruction row (number + text, already localized via getLocalizedTexts) with the
// matching Safari UI illustration below it. The illustrations embed their labels (e.g. "Gérer les extensions"
// / "Manage extensions"), so they are region-specific: FR uses the `version-b-instruction-*.png` cards and
// US/GB use the `*-us.png` variants. All cards were exported at 3x from the revamp Figma
// (file eRlJAWyVTduzNFOJmLWiQy — FR section 4653:12696, EN section 4653:12697) via the REST API; PNG is used
// rather than SVG because the frosted-glass cards rely on backdrop blur that SVG cannot represent. FR and EN
// share the same layout, hence the same aspect ratio per step.
// The step-1 address bar only shows "app.joko.com", so it is language-neutral: a single shared card is used
// across regions and reused by both the activation and authorization screens.
// NB: the A/B test itself is gated to Region.FR (see useShouldUseVerticalInstructionScreens), so the US/GB
// assets only render if/when the experiment is later extended to those regions.
const ASSET_BASE = '/assets/images/illustrations/mobile-safari-extension-installation';

type RegionalIllustration = {
    aspectRatio: number;
    sources: Record<Region, string>;
};

function localizedSources(frFile: string, usFile: string = frFile): Record<Region, string> {
    return {
        [Region.FR]: `${ASSET_BASE}/${frFile}`,
        [Region.US]: `${ASSET_BASE}/${usFile}`,
        [Region.GB]: `${ASSET_BASE}/${usFile}`,
    };
}

const addressBarIllustration: RegionalIllustration = {
    aspectRatio: 930 / 416,
    sources: localizedSources('version-b-instruction-activation-1.png'),
};

export const verticalActivationIllustrations: Record<1 | 2 | 3, RegionalIllustration> = {
    1: addressBarIllustration,
    2: {
        aspectRatio: 931 / 418,
        sources: localizedSources(
            'version-b-instruction-activation-2.png',
            'version-b-instruction-activation-2-us.png'
        ),
    },
    3: {
        aspectRatio: 931 / 385,
        sources: localizedSources(
            'version-b-instruction-activation-3.png',
            'version-b-instruction-activation-3-us.png'
        ),
    },
};

export const verticalAuthorizationFallbackIllustrations: Record<1 | 2 | 3 | 4, RegionalIllustration> = {
    1: addressBarIllustration,
    2: {
        aspectRatio: 931 / 517,
        sources: localizedSources(
            'version-b-instruction-authorization-2.png',
            'version-b-instruction-authorization-2-us.png'
        ),
    },
    3: {
        aspectRatio: 922 / 570,
        sources: localizedSources(
            'version-b-instruction-authorization-3.png',
            'version-b-instruction-authorization-3-us.png'
        ),
    },
    4: {
        aspectRatio: 922 / 570,
        sources: localizedSources(
            'version-b-instruction-authorization-4.png',
            'version-b-instruction-authorization-4-us.png'
        ),
    },
};

export function VerticalInstructions({ children }: { children: React.ReactNode }) {
    const steps = React.Children.toArray(children).filter(Boolean);
    return (
        <VerticalInstructionsVariantContext.Provider value={true}>
            <View style={styles.containerVerticalInstructions}>
                {steps.map((step, index) => (
                    <React.Fragment key={index}>
                        {index > 0 ? <View style={styles.stepDivider} /> : null}
                        {step}
                    </React.Fragment>
                ))}
            </View>
        </VerticalInstructionsVariantContext.Provider>
    );
}

export function VerticalInstructionStep({
    illustration,
    children,
    hasFullTopPadding = false,
}: {
    illustration: RegionalIllustration;
    children: React.ReactNode;
    // Authorization steps 3 & 4 keep the original (larger) top padding; their bottom padding is still reduced.
    hasFullTopPadding?: boolean;
}) {
    const uri = illustration.sources[getRegion()];
    // react-native-web 0.14 does not support the `aspectRatio` style (it predates the CSS `aspect-ratio`
    // property), so an <Image> with `width: '100%'` and no explicit height collapses to height 0 and looks
    // missing. We reserve the correct height with the padding-bottom percentage trick (height = width /
    // aspectRatio) on a wrapper View, then absolutely fill it with the image.
    const paddingBottom = `${100 / illustration.aspectRatio}%` as `${number}%`;
    return (
        <View style={styles.containerVerticalStep}>
            {children}
            <View style={[styles.illustrationCard, hasFullTopPadding && styles.illustrationCardFullTopPadding]}>
                <View style={[styles.illustrationFrame, { paddingBottom }]}>
                    <Image source={{ uri }} style={styles.illustration} resizeMode="contain" />
                </View>
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    containerVerticalInstructions: {
        alignSelf: 'stretch',
        gap: 16,
        paddingHorizontal: 16,
        paddingVertical: 16,
        borderWidth: 1,
        borderRadius: 12,
        borderColor: color.mercury,
    },
    // Light-gray separator between steps. It stays inside the container padding so it does not reach the
    // rounded border, and the container `gap` keeps it evenly spaced from each step.
    stepDivider: {
        height: 1,
        backgroundColor: color.mercury,
    },
    containerVerticalStep: {
        alignSelf: 'stretch',
        gap: 8,
    },
    illustrationCard: {
        alignSelf: 'stretch',
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 8,
        backgroundColor: color.white,
    },
    illustrationCardFullTopPadding: {
        paddingTop: 12,
    },
    illustrationFrame: {
        width: '100%',
    },
    illustration: {
        ...StyleSheet.absoluteFillObject,
        width: '100%',
        height: '100%',
    },
});
